import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { db, storage, subscribeToPush, getExistingSubscription } from "@/config/firebase";
import { collection, addDoc, query, orderBy, limit, onSnapshot, serverTimestamp, updateDoc, doc, arrayUnion, arrayRemove, getDocs, where } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { Button } from "@/components/ui/button";
import { Send, Image as ImageIcon, ArrowLeft, Save, Smile, Sparkles, Loader2, X, Download, MoreVertical, Bell, CheckCircle, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import EmojiPicker from 'emoji-picker-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || (import.meta && import.meta.env && import.meta.env.REACT_APP_BACKEND_URL);

// Quick reaction emojis for messages
const REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🔥', '🏀', '🎉'];

// Persistent image cache using localStorage + memory
const imageCache = new Map();
const CACHE_KEY = 'chat_image_cache';
const CACHE_MAX_SIZE = 50; // Max number of images to cache

// Load cache from localStorage on init
try {
  const stored = localStorage.getItem(CACHE_KEY);
  if (stored) {
    const parsed = JSON.parse(stored);
    Object.entries(parsed).forEach(([key, value]) => {
      imageCache.set(key, value);
    });
    console.log(`[ChatImage] Loaded ${imageCache.size} cached images`);
  }
} catch (e) {
  console.log('[ChatImage] Cache load failed:', e);
}

// Save cache to localStorage
function saveImageCache() {
  try {
    // Convert Map to object, limit size
    const entries = Array.from(imageCache.entries()).slice(-CACHE_MAX_SIZE);
    const obj = Object.fromEntries(entries);
    localStorage.setItem(CACHE_KEY, JSON.stringify(obj));
  } catch (e) {
    console.log('[ChatImage] Cache save failed:', e);
  }
}

// Helper to get cached or direct URL
function getCachedImageUrl(imageUrl) {
  if (!imageUrl) return null;
  if (imageCache.has(imageUrl)) return imageCache.get(imageUrl);
  if (!imageUrl.startsWith('chunked:')) {
    imageCache.set(imageUrl, imageUrl);
    return imageUrl;
  }
  return null;
}

function ChatImage({ imageUrl, onClick, className }) {
  // Get cached URL on initial render
  const initialCachedUrl = getCachedImageUrl(imageUrl);
  
  const [loadedUrl, setLoadedUrl] = useState(initialCachedUrl);
  const [loading, setLoading] = useState(!initialCachedUrl && imageUrl?.startsWith('chunked:'));
  const [error, setError] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);
  const hasFetchedRef = useRef(!!initialCachedUrl);

  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' } // Start loading 200px before visible
    );
    
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    
    return () => observer.disconnect();
  }, []);

  // Fetch chunked images only when visible and not cached
  useEffect(() => {
    // Skip if not visible, not a chunked image, or already have URL
    if (!isVisible || !imageUrl || !imageUrl.startsWith('chunked:')) return;
    
    // Check cache again (might have been populated by another instance)
    const cached = imageCache.get(imageUrl);
    if (cached) {
      if (loadedUrl !== cached) {
        setTimeout(() => {
          setLoadedUrl(cached);
          setLoading(false);
        }, 0);
      }
      return;
    }
    
    // Skip if we already fetched this URL
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    
    // Parse image ID - format can be "chunked:imageId" or "chunked:imageId:chunkCount"
    const parts = imageUrl.split(':');
    const imgId = parts[1];
    
    console.log('[ChatImage] Fetching chunked image:', imgId);
    
    fetch(`${BACKEND_URL}/api/chat/image/${imgId}`)
      .then(res => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        return res.json();
      })
      .then(data => {
        if (data.image_b64) {
          const url = `data:image/png;base64,${data.image_b64}`;
          imageCache.set(imageUrl, url);
          saveImageCache(); // Persist to localStorage
          setLoadedUrl(url);
          setLoading(false);
          console.log('[ChatImage] Image loaded successfully:', imgId);
        } else {
          console.error('[ChatImage] No image data in response:', data);
          setError(true);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error('[ChatImage] Failed to load image:', imgId, err);
        setError(true);
        setLoading(false);
      });
  }, [imageUrl, loadedUrl, isVisible]);

  if (loading) {
    return (
      <div ref={containerRef} className={`${className} bg-white/10 flex items-center justify-center`}>
        <Loader2 className="animate-spin text-pink-400" size={32} />
      </div>
    );
  }

  if (error || !loadedUrl) {
    return (
      <div ref={containerRef} className={`${className} bg-white/10 flex items-center justify-center text-rose-200/50`}>
        <span className="text-sm">Bild konnte nicht geladen werden</span>
      </div>
    );
  }

  return (
    <img 
      ref={containerRef}
      src={loadedUrl} 
      alt="Bild" 
      className={className}
      onClick={() => onClick && onClick(loadedUrl)}
      loading="lazy"
    />
  );
}

export default function Chat() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [showReactionPicker, setShowReactionPicker] = useState(null); // message id or null
  const [uploading, setUploading] = useState(false);
  const [savingToGallery, setSavingToGallery] = useState(null);
  const [savedToGallery, setSavedToGallery] = useState(new Set()); // Track saved images
  const [showAIPrompt, setShowAIPrompt] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiRecipient, setAiRecipient] = useState("");
  const [generatingImage, setGeneratingImage] = useState(false);
  const [editingImage, setEditingImage] = useState(null); // {id, url} of image being edited
  const [showMenu, setShowMenu] = useState(false);
  const [canInstall, setCanInstall] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [installInstructions, setInstallInstructions] = useState('');
  const [chatUrlForModal, setChatUrlForModal] = useState('');
  const [pushEnabled, setPushEnabled] = useState(false);
  const [registeringPush, setRegisteringPush] = useState(false);
  const [firstUnreadId, setFirstUnreadId] = useState(null);
  const [hasScrolledToUnread, setHasScrolledToUnread] = useState(false);
  const messagesEndRef = useRef(null);
  const firstUnreadRef = useRef(null);
  const fileInputRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const prevMsgCount = useRef(0);
  const didInitialScroll = useRef(false);

  // PWA Install prompt listener
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setCanInstall(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Register Service Worker and check push status
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw-chat.js')
        .then(reg => {
          console.log('Chat SW registered:', reg.scope);
        })
        .catch(err => console.log('Chat SW registration failed:', err));
    }
    
    // Check actual push subscription status (not just permission)
    const checkPushStatus = async () => {
      // First check if permission is granted
      if (!('Notification' in window) || Notification.permission !== 'granted') {
        setPushEnabled(false);
        return;
      }
      
      // Check if we have an actual subscription
      try {
        const sub = await getExistingSubscription();
        if (sub && sub.endpoint) {
          // Verify subscription exists on backend
          const response = await fetch(`${BACKEND_URL}/api/debug/push_subscriptions`);
          if (response.ok) {
            const data = await response.json();
            const userSub = data.subscriptions?.find(s => s.user_id === user?.uid);
            setPushEnabled(!!userSub);
          } else {
            setPushEnabled(false);
          }
        } else {
          setPushEnabled(false);
        }
      } catch (e) {
        console.log('[Push] Check failed:', e);
        setPushEnabled(false);
      }
    };
    
    if (user) {
      checkPushStatus();
    }
  }, [user]);

  // Register push subscription when user is available and push is enabled
  useEffect(() => {
    if (!user || !pushEnabled) return;
    
    const registerPushSubscription = async () => {
      try {
        const subscription = await getExistingSubscription();
        if (subscription) {
          // Get user name from user object or email
          const userName = user.displayName || user.email?.split('@')[0] || 'User';
          
          // Send subscription to backend
          const response = await fetch(`${BACKEND_URL}/api/push/subscribe`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: user.uid,
              user_name: userName,
              endpoint: subscription.endpoint,
              keys: subscription.keys
            })
          });
          
          if (response.ok) {
            console.log('Push subscription registered with backend');
          }
        }
      } catch (err) {
        console.error('Failed to register push subscription:', err);
      }
    };
    
    registerPushSubscription();
  }, [user, pushEnabled]);

  // Enable push notifications
  const enablePushNotifications = async () => {
    if (!('Notification' in window)) {
      alert('Dein Browser unterstützt keine Benachrichtigungen');
      return;
    }
    
    setRegisteringPush(true);
    
    try {
      // First request notification permission
      const permission = await Notification.requestPermission();
      
      if (permission !== 'granted') {
        alert('⚠️ Berechtigung wurde nicht erteilt.');
        setRegisteringPush(false);
        setShowMenu(false);
        return;
      }
      
      // Check if service workers are available
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        // Permission granted but no SW - still mark as enabled
        setPushEnabled(true);
        new Notification('✅ Browser-Benachrichtigungen aktiviert!', {
          body: 'Push-Notifications werden nach Deployment verfügbar sein.',
          icon: '/icon-192.png'
        });
        setRegisteringPush(false);
        setShowMenu(false);
        return;
      }
      
      // Try to register service worker
      try {
        await navigator.serviceWorker.register('/sw-chat.js');
        await navigator.serviceWorker.ready;
      } catch (swError) {
        console.warn('Service worker failed:', swError);
        setPushEnabled(true);
        new Notification('✅ Browser-Benachrichtigungen aktiviert!', {
          body: 'Push-Notifications werden nach Deployment verfügbar sein.',
          icon: '/icon-192.png'
        });
        setRegisteringPush(false);
        setShowMenu(false);
        return;
      }
      
      // Subscribe to push notifications
      const subscription = await subscribeToPush();
      
      if (subscription && subscription.endpoint && subscription.keys && user) {
        const userName = user.displayName || user.email?.split('@')[0] || 'User';
        
        // Send subscription to backend
        const response = await fetch(`${BACKEND_URL}/api/push/subscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: user.uid,
            user_name: userName,
            endpoint: subscription.endpoint,
            keys: subscription.keys
          })
        });
        
        if (response.ok) {
          setPushEnabled(true);
          new Notification('🔔 Push-Benachrichtigungen aktiviert!', {
            body: 'Du wirst jetzt über neue Chat-Nachrichten informiert.',
            icon: '/icon-192.png'
          });
        } else {
          const error = await response.json().catch(() => ({}));
          console.error('Backend subscription error:', error);
          setPushEnabled(false);
          alert('Fehler beim Registrieren der Benachrichtigungen:\n' + (error.detail || 'Unbekannter Fehler'));
        }
      } else {
        // No subscription possible - don't mark as enabled
        console.warn('Push subscription failed, not marking as enabled');
        setPushEnabled(false);
        if (Notification.permission === 'granted') {
          alert('⚠️ Browser-Berechtigung erteilt, aber Push konnte nicht aktiviert werden.\n\nBitte versuche es erneut.');
        } else {
          alert('Push-Benachrichtigungen konnten nicht aktiviert werden.');
        }
      }
    } catch (err) {
      console.error('Failed to enable push:', err);
      setPushEnabled(false);
      alert('Fehler beim Aktivieren der Benachrichtigungen:\n' + err.message);
    }
    
    setRegisteringPush(false);
    setShowMenu(false);
  };

  const installChatApp = async () => {
    // Chat kann IMMER separat installiert werden (eigene PWA)
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);
    
    // Dynamic URL - works in preview AND production
    const chatUrl = window.location.origin + '/chat.html';
    setChatUrlForModal(chatUrl);
    
    if (deferredPrompt) {
      // Native install prompt available
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setCanInstall(false);
        setDeferredPrompt(null);
      }
    } else {
      let instructions = '';
      if (isIOS) {
        instructions = `📱 Chat-Icon für iPhone/iPad:

1. Tippe unten auf "Chat öffnen"

2. Tippe auf das Teilen-Symbol
   (Quadrat mit Pfeil, unten)

3. Scrolle und tippe:
   "Zum Home-Bildschirm"

4. Tippe "Hinzufügen"

✅ "RS5 Chat" erscheint auf deinem Homescreen!`;
      } else if (isAndroid) {
        instructions = `📱 Chat-Icon für Android:

1. Tippe unten auf "Chat öffnen"

2. Tippe auf ⋮ (drei Punkte oben)

3. Wähle "Zum Startbildschirm"
   oder "App installieren"

✅ "RS5 Chat" erscheint auf deinem Homescreen!`;
      } else {
        instructions = `💻 Chat-Icon für Desktop:

1. Tippe unten auf "Chat öffnen"

2. Klicke auf das ⊕ Symbol
   in der Adressleiste

3. Oder: Menü → "Installieren"

✅ "RS5 Chat" wird installiert!`;
      }
      setInstallInstructions(instructions);
      setShowInstallModal(true);
    }
    setShowMenu(false);
  };

  const testNotification = () => {
    if (!('Notification' in window)) {
      alert('Dein Browser unterstützt keine Benachrichtigungen');
      return;
    }
    
    if (Notification.permission !== 'granted') {
      enablePushNotifications();
    } else {
      new Notification('🔔 Test erfolgreich!', {
        body: 'Push-Benachrichtigungen funktionieren. Du wirst bei neuen Nachrichten informiert.',
        icon: '/icon-192.png',
        tag: 'test-notification'
      });
    }
    setShowMenu(false);
  };

  // Load users
  useEffect(() => {
    (async () => {
      const usersSnap = await getDocs(collection(db, "users"));
      setUsers(usersSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    })();
  }, []);

  // Check which images are already saved to gallery
  useEffect(() => {
    if (!messages.length) return;
    
    const checkSavedImages = async () => {
      const imageMessages = messages.filter(m => m.type === 'image' || m.type === 'ai_image');
      if (!imageMessages.length) return;
      
      const gallerySnap = await getDocs(collection(db, "team_gallery"));
      const savedIds = new Set(gallerySnap.docs.map(d => d.data().chat_message_id));
      
      const alreadySaved = imageMessages
        .filter(m => savedIds.has(m.id))
        .map(m => m.id);
      
      if (alreadySaved.length > 0) {
        setSavedToGallery(prev => new Set([...prev, ...alreadySaved]));
      }
    };
    
    checkSavedImages();
  }, [messages]);

  // Listen to messages with better efficiency
  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, "team_chat"),
      orderBy("created_at", "desc"),
      limit(100)
    );
    
    let isFirstLoad = true;
    
    const unsub = onSnapshot(q, (snap) => {
      const msgs = snap.docs.map(d => ({ id: d.id, ...d.data() })).reverse();
      setMessages(msgs);
      
      // Only find unread on first load
      if (isFirstLoad) {
        // Find first unread message for scroll position
        const unreadMsg = msgs.find(m => 
          m.sender_id !== user.uid && !m.read_by?.includes(user.uid)
        );
        
        if (unreadMsg) {
          console.log('[Chat] Found unread message:', unreadMsg.id);
          setFirstUnreadId(unreadMsg.id);
        }
        isFirstLoad = false;
      }
      
      // Mark messages as read (batch update for efficiency)
      const unreadDocs = snap.docs.filter(d => {
        const data = d.data();
        return data.sender_id !== user.uid && !data.read_by?.includes(user.uid);
      });
      
      // Mark first 10 unread messages (visible ones)
      unreadDocs.slice(0, 10).forEach(d => {
        updateDoc(doc(db, "team_chat", d.id), {
          read_by: arrayUnion(user.uid)
        }).catch(() => {});
      });
    });
    
    return () => unsub();
  }, [user]);

  // Scroll handling: initial load jumps to unread/bottom, new messages auto-scroll down
  useEffect(() => {
    if (messages.length === 0) return;
    const container = messagesContainerRef.current;

    // Initial load: jump to first unread, otherwise straight to the bottom
    if (!didInitialScroll.current) {
      const timer = setTimeout(() => {
        if (firstUnreadId && !hasScrolledToUnread) {
          const el = document.querySelector(`[data-message-id="${firstUnreadId}"]`);
          (el || firstUnreadRef.current)?.scrollIntoView({ behavior: 'auto', block: 'center' });
          setHasScrolledToUnread(true);
        } else {
          messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
        }
        didInitialScroll.current = true;
        prevMsgCount.current = messages.length;
      }, 200);
      return () => clearTimeout(timer);
    }

    // New messages after initial load: scroll down if it's mine or I'm near the bottom
    if (messages.length > prevMsgCount.current) {
      const last = messages[messages.length - 1];
      const isMine = last?.sender_id === user?.uid;
      let nearBottom = true;
      if (container) {
        const dist = container.scrollHeight - container.scrollTop - container.clientHeight;
        nearBottom = dist < 300;
      }
      if (isMine || nearBottom) {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    }
    prevMsgCount.current = messages.length;
  }, [messages, firstUnreadId, hasScrolledToUnread, user]);

  const getUserName = (uid) => {
    const u = users.find(x => (x.auth_uid || x.id) === uid);
    return u?.display_name || u?.name || 'Unbekannt';
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !user) return;
    
    try {
      // Send via backend to bypass Firebase rules
      const response = await fetch(`${BACKEND_URL}/api/chat/send_message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: newMessage.trim(),
          sender_id: user.uid,
          sender_name: getUserName(user.uid)
        })
      });
      
      if (!response.ok) {
        throw new Error('Nachricht konnte nicht gesendet werden');
      }
      
      setNewMessage("");
      setShowEmoji(false);
    } catch (err) {
      console.error('Send message error:', err);
      alert('Fehler beim Senden der Nachricht');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    
    setUploading(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result.split(',')[1];
        
        // Upload to backend
        const response = await fetch(`${BACKEND_URL}/api/chat/upload_image`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            image_base64: base64,
            sender_id: user.uid,
            sender_name: getUserName(user.uid),
            filename: file.name
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          // Message already created by backend
        } else {
          alert('Bild konnte nicht hochgeladen werden');
        }
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error('Upload error:', err);
      setUploading(false);
      alert('Fehler beim Upload');
    }
  };

  const saveToGallery = async (messageId, imageUrl) => {
    // Check if already saved in this session
    if (savedToGallery.has(messageId)) {
      return; // Already saved, button shows checkmark
    }
    
    setSavingToGallery(messageId);
    try {
      // Prüfe ob Bild bereits in Galerie existiert
      const existingQuery = query(
        collection(db, "team_gallery"),
        where("chat_message_id", "==", messageId)
      );
      const existingDocs = await getDocs(existingQuery);
      
      if (!existingDocs.empty) {
        // Already in gallery - mark as saved
        setSavedToGallery(prev => new Set([...prev, messageId]));
        setSavingToGallery(null);
        return;
      }
      
      // Speichere in Galerie
      await addDoc(collection(db, "team_gallery"), {
        image_url: imageUrl,
        chat_message_id: messageId,
        saved_by: user.uid,
        saved_by_name: getUserName(user.uid),
        created_at: serverTimestamp(),
        tags: [],
        persons: [],
        faces_detected: false
      });
      
      // Mark as saved - show green checkmark
      setSavedToGallery(prev => new Set([...prev, messageId]));
      
    } catch (err) {
      console.error('Save to gallery error:', err);
      alert('Fehler beim Speichern');
    }
    setSavingToGallery(null);
  };

  // Add or remove emoji reaction to a message
  const toggleReaction = async (messageId, emoji) => {
    if (!user) return;
    
    try {
      const msgRef = doc(db, "team_chat", messageId);
      const msg = messages.find(m => m.id === messageId);
      
      // Check if user already reacted with this emoji
      const reactions = msg?.reactions || {};
      const emojiReactions = reactions[emoji] || [];
      const hasReacted = emojiReactions.includes(user.uid);
      
      if (hasReacted) {
        // Remove reaction
        await updateDoc(msgRef, {
          [`reactions.${emoji}`]: arrayRemove(user.uid)
        });
      } else {
        // Add reaction
        await updateDoc(msgRef, {
          [`reactions.${emoji}`]: arrayUnion(user.uid)
        });
      }
      
      setShowReactionPicker(null);
    } catch (err) {
      console.error('Reaction error:', err);
    }
  };

  // Get reaction counts for a message
  const getReactionCounts = (reactions) => {
    if (!reactions) return [];
    return Object.entries(reactions)
      .filter(([_, users]) => users && users.length > 0)
      .map(([emoji, users]) => ({
        emoji,
        count: users.length,
        hasReacted: users.includes(user?.uid)
      }));
  };

  const onEmojiClick = (emoji) => {
    setNewMessage(prev => prev + emoji);
    setShowEmoji(false);
  };

  // Start editing an existing image
  const startEditImage = (msgId, imageUrl) => {
    setEditingImage({ id: msgId, url: imageUrl });
    setShowAIPrompt(true);
    setAiPrompt('');
  };

  // AI Image Generation (Prompt-to-Image or Image Edit)
  const generateAIImage = async () => {
    if (!aiPrompt.trim() || !user) return;
    
    setGeneratingImage(true);
    try {
      // Prepare request body
      const requestBody = {
        prompt: aiPrompt.trim(),
        sender_id: user.uid,
        recipient_id: aiRecipient || 'team',
        sender_label: getUserName(user.uid)
      };
      
      // If editing an existing image, add source image data
      if (editingImage?.url) {
        // For chunked images, we need to fetch the base64 first
        let sourceImageB64 = '';
        
        if (editingImage.url.startsWith('chunked:')) {
          const parts = editingImage.url.split(':');
          const imgId = parts[1];
          const res = await fetch(`${BACKEND_URL}/api/chat/image/${imgId}`);
          const imgData = await res.json();
          sourceImageB64 = imgData.image_b64 || '';
        } else if (editingImage.url.startsWith('data:')) {
          sourceImageB64 = editingImage.url.split(',')[1] || '';
        } else {
          // External URL - try to convert to base64
          try {
            const imgRes = await fetch(editingImage.url);
            const blob = await imgRes.blob();
            sourceImageB64 = await new Promise((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result.split(',')[1]);
              reader.readAsDataURL(blob);
            });
          } catch (e) {
            console.warn('Could not fetch source image, generating new one');
          }
        }
        
        if (sourceImageB64) {
          requestBody.source_image_b64 = sourceImageB64;
          requestBody.edit_mode = true;
        }
      }
      
      // Step 1: Generate the image
      const response = await fetch(`${BACKEND_URL}/api/generate_image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Bildgenerierung fehlgeschlagen');
      }
      
      const data = await response.json();
      
      if (data.success && data.image_b64) {
        // Step 2: Send to chat via backend (bypasses Firebase rules)
        const recipientName = aiRecipient ? getUserName(aiRecipient) : null;
        
        // Build prompt text for chat message
        let promptText = aiPrompt.trim();
        if (editingImage) {
          promptText = `[Bearbeitet] ${promptText}`;
        }
        
        const chatResponse = await fetch(`${BACKEND_URL}/api/chat/send_ai_image`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            image_b64: data.image_b64,
            prompt: promptText,
            sender_id: user.uid,
            sender_name: getUserName(user.uid),
            recipient_id: aiRecipient || 'team',
            recipient_name: recipientName
          })
        });
        
        if (!chatResponse.ok) {
          throw new Error('Bild konnte nicht im Chat gepostet werden');
        }
        
        setShowAIPrompt(false);
        setAiPrompt("");
        setAiRecipient("");
        setEditingImage(null);
      } else {
        throw new Error('Kein Bild generiert');
      }
    } catch (err) {
      console.error('AI Image generation error:', err);
      alert(`Fehler: ${err.message}`);
    }
    setGeneratingImage(false);
  };

  const formatTime = (timestamp) => {
    if (!timestamp?.toDate) return '';
    const date = timestamp.toDate();
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) + ' ' + 
           date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-[#0a0608] text-rose-50 flex flex-col">
      {/* Header */}
      <header className="px-4 py-3 bg-[#12090c] border-b border-white/5 flex items-center gap-3 sticky top-0 z-10">
        <button onClick={() => navigate('/')} className="p-1">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <h1 className="font-semibold">Team Chat</h1>
          <p className="text-xs text-rose-200/50">{users.length} Mitglieder</p>
        </div>
        
        {/* Menu Button */}
        <div className="relative">
          <button 
            onClick={() => setShowMenu(!showMenu)}
            className="p-2 rounded-lg hover:bg-white/10"
          >
            <MoreVertical size={18} />
          </button>
          
          {showMenu && (
            <div className="absolute right-0 top-full mt-1 bg-[#1a0d11] border border-white/10 rounded-lg shadow-xl min-w-[200px] z-50">
              <button
                onClick={installChatApp}
                className="w-full px-4 py-3 text-left text-sm hover:bg-white/10 flex items-center gap-2 border-b border-white/5"
              >
                <Download size={16} className="text-pink-400" />
                Zum Homescreen
              </button>
              <button
                onClick={pushEnabled ? testNotification : enablePushNotifications}
                disabled={registeringPush}
                className="w-full px-4 py-3 text-left text-sm hover:bg-white/10 flex items-center gap-2"
              >
                {registeringPush ? (
                  <Loader2 size={16} className="text-yellow-400 animate-spin" />
                ) : pushEnabled ? (
                  <CheckCircle size={16} className="text-green-400" />
                ) : (
                  <Bell size={16} className="text-yellow-400" />
                )}
                {registeringPush ? 'Aktiviere...' : pushEnabled ? 'Push aktiv ✓' : 'Push aktivieren'}
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Click outside to close menu */}
      {showMenu && (
        <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
      )}

      {/* Messages */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto px-3 py-4 space-y-3 pb-32">
        {messages.length === 0 ? (
          <div className="text-center text-rose-200/40 py-8">
            <p>Noch keine Nachrichten</p>
            <p className="text-sm mt-1">Starte die Unterhaltung!</p>
          </div>
        ) : (
          messages.map((msg, idx) => {
            const isMe = msg.sender_id === user?.uid;
            const showSender = idx === 0 || messages[idx - 1]?.sender_id !== msg.sender_id;
            const isFirstUnread = msg.id === firstUnreadId;
            
            return (
              <div 
                key={msg.id} 
                ref={isFirstUnread ? firstUnreadRef : null}
                data-message-id={msg.id}
                className={`flex ${isMe ? 'justify-end' : 'justify-start'} relative`}
              >
                {/* Unread marker */}
                {isFirstUnread && (
                  <div className="absolute left-0 right-0 -top-6 flex items-center justify-center">
                    <div className="bg-pink-600/80 text-white text-xs px-4 py-1 rounded-full shadow-lg">
                      ↓ Neue Nachrichten
                    </div>
                  </div>
                )}
                <div className={`max-w-[80%] ${isMe ? 'items-end' : 'items-start'}`}>
                  {showSender && !isMe && (
                    <p className="text-xs text-rose-200/50 mb-1 ml-1">{msg.sender_name}</p>
                  )}
                  <div className={`rounded-2xl px-3 py-2 ${
                    isMe 
                      ? 'bg-rose-600/80 rounded-br-sm' 
                      : 'bg-[#1a0d11] rounded-bl-sm'
                  }`}>
                    {(msg.type === 'image' || msg.type === 'ai_image') ? (
                      <div className="space-y-2">
                        {msg.type === 'ai_image' && (
                          <div className="flex items-center gap-1 text-xs text-rose-200/60 mb-1">
                            <Sparkles size={12} className="text-purple-400" />
                            {msg.recipient_name ? (
                              <span>Shoutout an <strong className="text-purple-300">{msg.recipient_name}</strong></span>
                            ) : (
                              <span>AI-Bild</span>
                            )}
                          </div>
                        )}
                        <ChatImage 
                          imageUrl={msg.image_url}
                          imageId={msg.image_id}
                          className="rounded-lg max-w-full max-h-64 object-cover cursor-pointer"
                          onClick={(url) => window.open(url, '_blank')}
                        />
                        {msg.ai_prompt && (
                          <p className="text-xs text-rose-200/50 italic">&ldquo;{msg.ai_prompt}&rdquo;</p>
                        )}
                        <button
                          onClick={() => saveToGallery(msg.id, msg.image_url)}
                          disabled={savingToGallery === msg.id || savedToGallery.has(msg.id)}
                          className={`flex items-center gap-1 text-xs transition-colors ${
                            savedToGallery.has(msg.id)
                              ? 'text-green-400'
                              : 'text-rose-200/60 hover:text-rose-200'
                          }`}
                        >
                          {savedToGallery.has(msg.id) ? (
                            <>
                              <CheckCircle size={12} className="text-green-400" />
                              <span>Gespeichert ✓</span>
                            </>
                          ) : savingToGallery === msg.id ? (
                            <>
                              <Loader2 size={12} className="animate-spin" />
                              <span>Speichern...</span>
                            </>
                          ) : (
                            <>
                              <Save size={12} />
                              <span>In Galerie speichern</span>
                            </>
                          )}
                        </button>
                        {/* Edit Image Button */}
                        <button
                          onClick={() => startEditImage(msg.id, msg.image_url)}
                          className="flex items-center gap-1 text-xs text-purple-300/60 hover:text-purple-300 transition-colors"
                        >
                          <Sparkles size={12} />
                          <span>Bild bearbeiten</span>
                        </button>
                      </div>
                    ) : (
                      <p className="text-sm whitespace-pre-wrap break-words">{msg.text}</p>
                    )}
                    <p className={`text-xs mt-1 ${isMe ? 'text-white/50' : 'text-rose-200/30'}`}>
                      {formatTime(msg.created_at)}
                    </p>
                  </div>
                  
                  {/* Emoji Reactions Display */}
                  {getReactionCounts(msg.reactions).length > 0 && (
                    <div className={`flex flex-wrap gap-1 mt-1 ${isMe ? 'justify-end' : 'justify-start'}`}>
                      {getReactionCounts(msg.reactions).map(({ emoji, count, hasReacted }) => (
                        <button
                          key={emoji}
                          onClick={() => toggleReaction(msg.id, emoji)}
                          className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs transition-colors ${
                            hasReacted 
                              ? 'bg-rose-500/30 border border-rose-500/50' 
                              : 'bg-white/10 hover:bg-white/20'
                          }`}
                        >
                          <span>{emoji}</span>
                          <span className="text-rose-200/80">{count}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {/* Reaction Picker Button */}
                  <div className={`flex mt-1 ${isMe ? 'justify-end' : 'justify-start'}`}>
                    <button
                      onClick={() => setShowReactionPicker(showReactionPicker === msg.id ? null : msg.id)}
                      className="p-1 rounded-full hover:bg-white/10 transition-colors opacity-50 hover:opacity-100"
                    >
                      <Smile size={14} className="text-rose-200/60" />
                    </button>
                  </div>
                  
                  {/* Reaction Picker Popup */}
                  {showReactionPicker === msg.id && (
                    <div className={`absolute z-30 ${isMe ? 'right-0' : 'left-0'} mt-1 bg-[#1a0d11] border border-white/10 rounded-full px-2 py-1 flex gap-1 shadow-lg`}>
                      {REACTION_EMOJIS.map((emoji) => (
                        <button
                          key={emoji}
                          onClick={() => toggleReaction(msg.id, emoji)}
                          className="text-lg hover:scale-125 transition-transform p-0.5"
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* AI Prompt Modal */}
      {showAIPrompt && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => { setShowAIPrompt(false); setEditingImage(null); }}>
          <div 
            className="bg-[#12090c] border border-rose-500/30 rounded-xl p-4 w-full max-w-md"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold flex items-center gap-2">
                <Sparkles size={18} className="text-rose-400" />
                {editingImage ? 'Bild bearbeiten' : 'AI Bild generieren'}
              </h3>
              <button onClick={() => { setShowAIPrompt(false); setEditingImage(null); }} className="p-1">
                <X size={18} />
              </button>
            </div>
            
            {/* Show source image preview when editing */}
            {editingImage && (
              <div className="mb-3 p-2 bg-white/5 rounded-lg">
                <p className="text-xs text-rose-200/60 mb-2">Ausgangsbild:</p>
                <ChatImage 
                  imageUrl={editingImage.url}
                  className="w-full max-h-32 object-contain rounded"
                />
              </div>
            )}
            
            <div className="space-y-3">
              <div>
                <label className="text-xs text-rose-200/60 block mb-1">
                  {editingImage ? 'Was soll geändert werden?' : 'Beschreibe das Bild:'}
                </label>
                <textarea
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder={editingImage 
                    ? "z.B. Mache den Hintergrund blau, füge einen Ball hinzu..." 
                    : "z.B. Ein Basketballspieler macht einen Dunking bei Sonnenuntergang..."}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm resize-none h-24 focus:outline-none focus:border-rose-500/50"
                  autoFocus
                />
              </div>
              
              {!editingImage && (
                <div>
                  <label className="text-xs text-rose-200/60 block mb-1">Empfänger (optional):</label>
                  <select
                    value={aiRecipient}
                    onChange={(e) => setAiRecipient(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-500/50"
                  >
                    <option value="">Für das ganze Team</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>
                        {u.display_name || u.name || 'Unbekannt'}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              <Button
                onClick={generateAIImage}
                disabled={!aiPrompt.trim() || generatingImage}
                className="w-full bg-gradient-to-r from-rose-600 to-purple-600 hover:from-rose-700 hover:to-purple-700"
              >
                {generatingImage ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    {editingImage ? 'Bearbeite Bild...' : 'Generiere Bild...'}
                  </>
                ) : (
                  <>
                    <Sparkles size={16} className="mr-2" />
                    {editingImage ? 'Bild bearbeiten' : 'Bild generieren'}
                  </>
                )}
              </Button>
              
              <p className="text-xs text-rose-200/40 text-center">
                Powered by OpenAI GPT Image
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Emoji Picker - Full */}
      {showEmoji && (
        <div className="absolute bottom-20 left-2 right-2 z-20">
          <EmojiPicker 
            onEmojiClick={(emojiData) => {
              setNewMessage(prev => prev + emojiData.emoji);
              setShowEmoji(false);
            }}
            theme="dark"
            width="100%"
            height={350}
            searchPlaceHolder="Emoji suchen..."
            previewConfig={{ showPreview: false }}
            skinTonesDisabled
            lazyLoadEmojis
          />
        </div>
      )}

      {/* Input */}
      <div className="fixed bottom-16 left-0 right-0 bg-[#12090c] border-t border-white/5 p-2 px-3">
        <div className="flex items-center gap-1.5">
          <button 
            onClick={() => setShowEmoji(!showEmoji)}
            className="p-2 rounded-full bg-white/5 hover:bg-white/10 flex-shrink-0"
          >
            <Smile size={18} className="text-rose-200/60" />
          </button>
          
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageUpload}
            accept="image/*"
            className="hidden"
          />
          <button 
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="p-2 rounded-full bg-white/5 hover:bg-white/10 flex-shrink-0"
          >
            <ImageIcon size={18} className={uploading ? 'text-rose-200/30 animate-pulse' : 'text-rose-200/60'} />
          </button>
          
          {/* AI Image Generation Button */}
          <button 
            onClick={() => setShowAIPrompt(true)}
            className="p-2 rounded-full bg-gradient-to-r from-rose-600/20 to-purple-600/20 hover:from-rose-600/30 hover:to-purple-600/30 border border-rose-500/30 flex-shrink-0"
            data-testid="ai-image-btn"
          >
            <Sparkles size={18} className="text-rose-400" />
          </button>
          
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
            placeholder="Nachricht..."
            className="flex-1 min-w-0 bg-white/5 border border-white/10 rounded-full px-3 py-2 text-sm focus:outline-none focus:border-rose-500/50"
          />
          
          <Button
            onClick={sendMessage}
            disabled={!newMessage.trim()}
            className="p-2 rounded-full bg-rose-600 hover:bg-rose-700 disabled:opacity-50 flex-shrink-0"
            size="icon"
          >
            <Send size={18} />
          </Button>
        </div>
      </div>

      {/* Install Chat Modal */}
      {showInstallModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowInstallModal(false)}>
          <div 
            className="bg-[#12090c] border border-pink-500/30 rounded-xl p-4 w-full max-w-sm max-h-[80vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="font-semibold text-pink-300 mb-3">Chat-Shortcut erstellen</h3>
            <pre className="text-sm text-rose-200/80 whitespace-pre-wrap font-sans leading-relaxed">
              {installInstructions}
            </pre>
            
            {/* Button to open chat URL externally */}
            <a 
              href={chatUrlForModal}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full mt-4 py-3 bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-700 hover:to-rose-700 rounded-lg font-medium flex items-center justify-center gap-2 text-white"
            >
              <Download size={18} />
              Chat öffnen (extern)
            </a>
            
            <button 
              onClick={() => setShowInstallModal(false)}
              className="w-full mt-2 py-2 bg-white/10 hover:bg-white/20 rounded-lg font-medium text-rose-200/70"
            >
              Schließen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
